import React from 'react';
import {
  Box,
  Button,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Paper,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { getDocumentJoiSchema } from '../../validation/documentSchema';
import Footer from 'examples/Footer';
import MDDatePicker from 'components/MDDatePicker';
import { api } from '../../api';
import Swal from 'sweetalert2';
import CircularProgress from '@mui/material/CircularProgress';
import CloudDoneIcon from '@mui/icons-material/CloudDone';

// Removed global localStorage retrieval - now done inside component to get fresh values

const docTypes = [
  {
    label: 'Aadhaar No.',
    key: 'aadhaar',
    fields: [
      { name: 'number', label: 'Aadhaar No.', required: true, type: 'text' },
      { name: 'front', label: 'Aadhaar Front Image', required: true, type: 'file' },
      { name: 'back', label: 'Aadhaar Back Image', required: true, type: 'file' },
    ],
    showIssuedBy: false,
    showValidTill: false,
  },
  {
    label: 'PAN No.',
    key: 'pan',
    fields: [
      { name: 'number', label: 'PAN No.', required: true, type: 'text' },
      { name: 'front', label: 'PAN Front Image', required: true, type: 'file' },
      { name: 'back', label: 'PAN Back Image', required: true, type: 'file' },
    ],
    showIssuedBy: false,
    showValidTill: false,
  },
  {
    label: 'Voter Card No.',
    key: 'voter',
    fields: [
      { name: 'number', label: 'Voter Card No.', required: true, type: 'text' },
      { name: 'front', label: 'Voter Card Front Image', required: true, type: 'file' },
      { name: 'back', label: 'Voter Card Back Image', required: true, type: 'file' },
    ],
    showIssuedBy: false,
    showValidTill: false,
  },
  {
    label: 'Passport No.',
    key: 'passport',
    fields: [
      { name: 'number', label: 'Passport No.', required: true, type: 'text' },
      { name: 'front', label: 'Passport Front Image', required: true, type: 'file' },
      { name: 'back', label: 'Passport Back Image', required: true, type: 'file' },
      { name: 'issuedBy', label: 'Issued By', required: true, type: 'text' },
      { name: 'validTill', label: 'Valid Till', required: true, type: 'date' },
    ],
    showIssuedBy: true,
    showValidTill: true,
  },
  {
    label: 'Driving License No.',
    key: 'driving-liciense',
    fields: [
      { name: 'number', label: 'Driving License No.', required: true, type: 'text' },
      { name: 'front', label: 'Driving License Front Image', required: true, type: 'file' },
      { name: 'back', label: 'Driving License Back Image', required: true, type: 'file' },
      { name: 'issuedBy', label: 'Issued By', required: true, type: 'text' },
      { name: 'validTill', label: 'Valid Till', required: true, type: 'date' },
    ],
    showIssuedBy: true,
    showValidTill: true,
  },
  {
    label: 'Visa No.',
    key: 'visa',
    fields: [
      { name: 'number', label: 'Visa No.', required: true, type: 'text' },
      { name: 'front', label: 'Visa Front Image', required: true, type: 'file' },
      { name: 'back', label: 'Visa Back Image', required: true, type: 'file' },
      { name: 'issuedBy', label: 'Issued By', required: true, type: 'text' },
      { name: 'validTill', label: 'Valid Till', required: true, type: 'date' },
    ],
    showIssuedBy: true,
    showValidTill: true,
  },
];

export default function DocumentUpload({
  label = 'Upload Document',
  onChange,
  accept = '*',
  multiple = false,
  value,
  existingPatientDocuments = [],
  existingSpouseDocuments = [],
  existingPatientProfilePicture = null,
  existingSpouseProfilePicture = null,
  hasSpouseInfo = true, // Default to true for backward compatibility
}) {
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, touchedFields, isSubmitted },
  } = useForm({
    defaultValues: {
      patient: {
        aadhar: { number: '', front: null, back: null },
        passport: { number: '', front: null, back: null, issuedBy: '', validTill: '' },
        visa: { number: '', front: null, back: null, issuedBy: '', validTill: '' },
      },
      spouse: {
        aadhar: { number: '', front: null, back: null },
        passport: { number: '', front: null, back: null, issuedBy: '', validTill: '' },
        visa: { number: '', front: null, back: null, issuedBy: '', validTill: '' },
      },
    },
    mode: 'onBlur',
  });

  const [selectedDate, setSelectedDate] = React.useState(null);
  const [pageLoading, setPageLoading] = React.useState(true);
  const [uploading, setUploading] = React.useState({}); // { patient_aadhaar: false, ... }
  const [uploaded, setUploaded] = React.useState({}); // { patient_aadhaar: false, ... }
  const [validationErrors, setValidationErrors] = React.useState({}); // { patient_aadhaar: { number: '', ... } }
  const [profileImages, setProfileImages] = React.useState({ patient: null, spouse: null });
  const [profileImageErrors, setProfileImageErrors] = React.useState({ patient: '', spouse: '' });
  const [profileUploading, setProfileUploading] = React.useState({ patient: false, spouse: false });
  const [profileUploaded, setProfileUploaded] = React.useState({ patient: false, spouse: false });
  
  // Get nationality from localStorage for document highlighting
  const nationality = localStorage.getItem('nationality');

  // Helper function to check if a document type already exists
  const hasExistingDocument = (documentType, person) => {
    const existingDocs = person === 'spouse' ? existingSpouseDocuments : existingPatientDocuments;
    
    // Map document type keys to their corresponding API type IDs
    const typeMapping = {
      'aadhaar': 1,   // Aadhaar type ID
      'pan': 2,       // PAN type ID  
      'voter': 3,     // Voter/EPIC type ID
      'passport': 5,  // Passport type ID
      'driving-liciense': 4, // Driving License type ID
      'visa': 6,      // Visa type ID
    };
    
    const expectedTypeId = typeMapping[documentType];
    
    if (!expectedTypeId) {
      console.log(`Warning: Unknown document type ${documentType}`);
      return false;
    }
    
    const hasDocument = existingDocs.some(doc => {
      // Primary check: document_type.id
      if (doc.document_type?.id === expectedTypeId) {
        return true;
      }
      
      // Fallback: check if type name matches (case-insensitive)
      if (doc.type) {
        const docTypeLower = doc.type.toLowerCase();
        const documentTypeLower = documentType.toLowerCase();
        
        // Special handling for different document type variations
        if (documentType === 'aadhaar' && (docTypeLower.includes('aadhaar') || docTypeLower.includes('aadhar'))) {
          return true;
        }
        if (documentType === 'voter' && (docTypeLower.includes('voter') || docTypeLower.includes('epic'))) {
          return true;
        }
        if (documentType === 'driving-liciense' && (docTypeLower.includes('driving') || docTypeLower.includes('license'))) {
          return true;
        }
        
        // Generic check for exact matches
        if (docTypeLower.includes(documentTypeLower.replace('-liciense', '').replace('aadhaar', ''))) {
          return true;
        }
      }
      
      return false;
    });
    
    console.log(`Document check: ${documentType} (ID: ${expectedTypeId}) - Has existing: ${hasDocument}`);
    return hasDocument;
  };

  // Filter document types to hide those that already exist
  const getAvailableDocTypes = (person) => {
    return docTypes.filter(doc => !hasExistingDocument(doc.key, person));
  };

  const handleProfileImageUpload = async (type) => {
    // type: 'patient' or 'spouse'
    const file = profileImages[type];
    if (!file) {
      setProfileImageErrors((prev) => ({ ...prev, [type]: 'Please select an image to upload.' }));
      return;
    }
    setProfileImageErrors((prev) => ({ ...prev, [type]: '' }));
    setProfileUploading((prev) => ({ ...prev, [type]: true }));
    setProfileUploaded((prev) => ({ ...prev, [type]: false }));
    const id =
      type === 'spouse' ? localStorage.getItem('spouse_id') : localStorage.getItem('patient_id');

    const formData = new FormData();
    formData.append('file', file);
    formData.append('patient_id', id);
    try {
      await api.fileUploadApi.uploadProfileImage(formData);
      setProfileUploaded((prev) => ({ ...prev, [type]: true }));
      Swal.fire({
        icon: 'success',
        title: 'Profile Image Uploaded',
        text: `${type === 'spouse' ? 'Spouse' : 'Patient'} profile image uploaded successfully!`,
        showConfirmButton: false,
        timer: 1200,
      });
    } catch (error) {
      setProfileImageErrors((prev) => ({ ...prev, [type]: 'Upload failed. Please try again.' }));
      Swal.fire({
        icon: 'error',
        title: 'Upload failed',
        text: error.response?.data?.message || 'An error occurred during profile image upload.',
        showConfirmButton: true,
      });
    } finally {
      setProfileUploading((prev) => ({ ...prev, [type]: false }));
    }
  };

  React.useEffect(() => {
    // Simulate page loading
    const timer = setTimeout(() => setPageLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);

  // Helper to render a row for a document type (patient or spouse)
  const renderDocRow = (person, doc, idx) => {
    const key = `${person}_${doc.key}`;
    const docData = watch(`${person}.${doc.key}`);
    const errorsForDoc = validationErrors[key] || {};
    // Highlight logic for mandatory doc type based on nationality
    let highlight = false;
    let highlightColor = '#ffe082'; // Amber 200
    if (nationality && nationality.toLowerCase() === 'indian' && doc.key === 'aadhaar') {
      highlight = true;
      highlightColor = '#fff3e0'; // Light blue for Indian Aadhaar
    } else if (
      nationality &&
      nationality.toLowerCase() !== 'indian' &&
      (doc.key === 'passport' || doc.key === 'visa')
    ) {
      highlight = true;
      highlightColor = '#fff3e0'; // Light orange for foreign Passport/Visa
    }
    return (
      <TableRow
        key={person + doc.key}
        sx={
          highlight
            ? {
                borderLeft: '6px solid #00798E',
                background: highlightColor,
                animation: 'mandatoryRowPulse 1.5s infinite',
              }
            : {}
        }
      >
        <TableCell>
          <Controller
            name={`${person}.${doc.key}.number`}
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <>
                <TextField
                  {...field}
                  label={
                    <span>
                      {doc.label} <span style={{ color: 'red' }}>*</span>
                    </span>
                  }
                  // required
                  size="small"
                  fullWidth
                  error={!!errors?.[person]?.[doc.key]?.number || !!errorsForDoc.number}
                  helperText={
                    errors?.[person]?.[doc.key]?.number ? 'Required' : errorsForDoc.number
                  }
                />
              </>
            )}
          />
        </TableCell>
        <TableCell>
          <Controller
            name={`${person}.${doc.key}.front`}
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <>
                <input
                  accept={accept}
                  style={{ display: 'none' }}
                  id={`${person}-${doc.key}-front-upload`}
                  type="file"
                  onChange={(e) => field.onChange(e.target.files[0])}
                />
                <label htmlFor={`${person}-${doc.key}-front-upload`}>
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon sx={{ color: '#00798E' }} />}
                    sx={{ color: '#00798E', borderColor: '#00798E', minWidth: 120 }}
                  >
                    {watch(`${person}.${doc.key}.front`) ? 'Change File' : 'Upload Front'}
                  </Button>
                </label>
                {watch(`${person}.${doc.key}.front`) && (
                  <Typography variant="caption" ml={1}>
                    {watch(`${person}.${doc.key}.front`).name}
                  </Typography>
                )}
                {errorsForDoc.front && (
                  <Typography variant="caption" color="error" ml={1}>
                    {errorsForDoc.front}
                  </Typography>
                )}
              </>
            )}
          />
        </TableCell>
        <TableCell>
          <Controller
            name={`${person}.${doc.key}.back`}
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <>
                <input
                  accept={accept}
                  style={{ display: 'none' }}
                  id={`${person}-${doc.key}-back-upload`}
                  type="file"
                  onChange={(e) => field.onChange(e.target.files[0])}
                />
                <label htmlFor={`${person}-${doc.key}-back-upload`}>
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon sx={{ color: '#00798E' }} />}
                    sx={{ color: '#00798E', borderColor: '#00798E', minWidth: 120 }}
                  >
                    {watch(`${person}.${doc.key}.back`) ? 'Change File' : 'Upload Back'}
                  </Button>
                </label>
                {watch(`${person}.${doc.key}.back`) && (
                  <Typography variant="caption" ml={1}>
                    {watch(`${person}.${doc.key}.back`).name}
                  </Typography>
                )}
                {errorsForDoc.back && (
                  <Typography variant="caption" color="error" ml={1}>
                    {errorsForDoc.back}
                  </Typography>
                )}
              </>
            )}
          />
        </TableCell>
        {doc.showIssuedBy && (
          <TableCell>
            <Controller
              name={`${person}.${doc.key}.issuedBy`}
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={
                    <span>
                      Issued By <span style={{ color: 'red' }}>*</span>
                    </span>
                  }
                  // required
                  size="small"
                  fullWidth
                  error={!!errors?.[person]?.[doc.key]?.issuedBy || !!errorsForDoc.issuedBy}
                  helperText={
                    errors?.[person]?.[doc.key]?.issuedBy ? 'Required' : errorsForDoc.issuedBy
                  }
                />
              )}
            />
          </TableCell>
        )}
        {doc.showValidTill && (
          <TableCell>
            <Controller
              name={`${person}.${doc.key}.validTill`}
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <>
                  <MDDatePicker
                    value={field.value || null}
                    onChange={(date) => field.onChange(date)}
                    label={
                      <span>
                        Valid Till <span style={{ color: 'red' }}>*</span>
                      </span>
                    }
                    input={{
                      // required: true,
                      size: 'small',
                      fullWidth: true,
                      error: !!errors?.[person]?.[doc.key]?.validTill || !!errorsForDoc.validTill,
                      helperText: errors?.[person]?.[doc.key]?.validTill
                        ? 'Required'
                        : errorsForDoc.validTill,
                    }}
                  />
                  <br />
                  {errorsForDoc.validTill && (
                    <Typography variant="caption" color="error" ml={1}>
                      {errorsForDoc.validTill}
                    </Typography>
                  )}
                </>
              )}
            />
          </TableCell>
        )}
        <TableCell align="center">
          <IconButton
            color="primary"
            onClick={() => uploadDocument(person, doc)}
            disabled={uploading[key]}
          >
            {pageLoading ? (
              <CircularProgress size="30px" sx={{ color: '#00798E' }} />
            ) : uploading[key] ? (
              <CircularProgress size="30px" sx={{ color: '#00798E' }} />
            ) : uploaded[key] ? (
              <CloudDoneIcon sx={{ color: '#4CAF50' }} />
            ) : (
              <CloudUploadIcon />
            )}
          </IconButton>
        </TableCell>
      </TableRow>
    );
  };

  /* Add keyframes for pulse animation */
  const pulseStyles = `
    @keyframes mandatoryRowPulse {
      0% { box-shadow: 0 0 0 0 rgba(0,121,142,0.15); }
      70% { box-shadow: 0 0 0 8px rgba(0,121,142,0.08); }
      100% { box-shadow: 0 0 0 0 rgba(0,121,142,0.15); }
    }
  `;

  const uploadDocument = async (person, doc) => {
    const key = `${person}_${doc.key}`;
    let docData = { ...watch(`${person}.${doc.key}`) };
    // Convert validTill to string if it's a Date object
    if (doc.showValidTill && docData.validTill && typeof docData.validTill !== 'string') {
      try {
        // Try to format as YYYY-MM-DD
        const d = new Date(docData.validTill);
        if (!isNaN(d)) {
          docData.validTill = d.toISOString().slice(0, 10);
        }
      } catch (e) {
        // fallback: just stringify
        docData.validTill = String(docData.validTill);
      }
    }
    // Joi validation
    const schema = getDocumentJoiSchema(doc);
    const { error } = schema.validate(docData, { abortEarly: false });
    if (error) {
      // Map errors to fields
      const fieldErrors = {};
      error.details.forEach((detail) => {
        const field = detail.path[0];
        fieldErrors[field] = detail.message;
      });
      setValidationErrors((prev) => ({ ...prev, [key]: fieldErrors }));
      return;
    } else {
      setValidationErrors((prev) => ({ ...prev, [key]: {} }));
    }
    setUploading((prev) => ({ ...prev, [key]: true }));
    setUploaded((prev) => ({ ...prev, [key]: false }));
    console.log('Uploading document data:', docData.front);
    const patient_id = localStorage.getItem('patient_id');
    const spouse_id = localStorage.getItem('spouse_id');
    
    console.log('Debug: Document upload for', person);
    console.log('Patient ID from localStorage:', patient_id);
    console.log('Spouse ID from localStorage:', spouse_id);
    
    const formData = new FormData();
    formData.append('file1', docData.front); // front image
    formData.append('file2', docData.back); // back image
    
    // Set contact_id based on person
    let contactId;
    if (person === 'spouse') {
      contactId = spouse_id;
      formData.append('contact_id', spouse_id);
    } else {
      contactId = patient_id;
      formData.append('contact_id', patient_id);
    }
    
    console.log(`Using contact_id ${contactId} for ${person} document upload`);
    formData.append('document_number', docData.number);
    let sendableType = doc.key;
    if(doc.key === 'voter') {
      sendableType = 'epic'; // API expects this exact spelling
    }
    formData.append('type', sendableType); // pan / aadhar / visa / passport
    formData.append('is_default', 'Y');
    formData.append('file_sub_type_1', `front_${doc.key}`);
    formData.append('file_sub_type_2', `back_${doc.key}`);

    if (doc.showIssuedBy) {
      formData.append('issued_by', docData.issuedBy);
    }

    if (doc.showValidTill) {
      formData.append('valid_till', docData.validTill);
    }
    console.log('Uploading document:', formData);

    try {
      const response = await api.fileUploadApi.upload(formData);
      console.log('Upload successful:', response.data || response);
      setUploaded((prev) => ({ ...prev, [key]: true }));
      Swal.fire({
        icon: 'success',
        title: 'Upload successful',
        text: `Successfully uploaded ${doc.label} for ${person}`,
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      console.error('Upload failed:', error);
      Swal.fire({
        icon: 'error',
        title: 'Upload failed',
        text:
          (error.response?.data?.messages
            ? Object.values(error.response.data.messages).join(', ')
            : error.response?.data?.message) || 'An error occurred during document upload.',
        showConfirmButton: true,
      });
    } finally {
      setUploading((prev) => ({ ...prev, [key]: false }));
    }
  };

  // Helper to check if all mandatory uploads are done
  const isMandatoryUploadsDone = () => {
    const isIndian = nationality && nationality.toLowerCase() === 'indian';
    // Profile images
    const patientProfile = profileUploaded.patient;
    const spouseProfile = profileUploaded.spouse;
    // Aadhaar (for Indian)
    const aadhaar = uploaded['patient_aadhaar'];
    const spouseAadhaar = uploaded['spouse_aadhaar'];
    // Passport & Visa (for foreign)
    const passport = uploaded['patient_passport'];
    const spousePassport = uploaded['spouse_passport'];
    const visa = uploaded['patient_visa'];
    const spouseVisa = uploaded['spouse_visa'];

    if (isIndian) {
      // return patientProfile && spouseProfile && aadhaar && spouseAadhaar;
      return aadhaar && spouseAadhaar;
    } else {
      // return patientProfile && spouseProfile && passport && visa && spousePassport && spouseVisa;
      return passport && visa && spousePassport && spouseVisa;
    }
  };

  return (
    <form>
      {pageLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress size="50px" sx={{ color: '#00798E' }} />
        </Box>
      ) : (
        <Box my={3}>
          {/* Profile Image Upload Section */}
          <Box display="flex" gap={4} mb={4}>
            {/* Patient Profile Image - Only show if no existing profile picture */}
            {!existingPatientProfilePicture && (
              <Box
                sx={{
                  border: '2px dashed #00798E',
                  borderRadius: 2,
                  width: 180,
                  height: 180,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  background: '#F7F9FA',
                  position: 'relative',
                }}
                onClick={() => document.getElementById('patient-profile-upload').click()}
              >
                <input
                  id="patient-profile-upload"
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setProfileImages((prev) => ({ ...prev, patient: file }));
                      setProfileImageErrors((prev) => ({ ...prev, patient: '' }));
                      setProfileUploaded((prev) => ({ ...prev, patient: false }));
                    }
                  }}
                />
                {profileImages.patient ? (
                  <img
                    src={URL.createObjectURL(profileImages.patient)}
                    alt="Patient Profile"
                    style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: '50%' }}
                  />
                ) : (
                  <>
                    <CloudUploadIcon sx={{ fontSize: 40, color: '#00798E' }} />
                    <Typography variant="body2" sx={{ color: 'white' }} mt={1}>
                      Select Patient Photo
                    </Typography>
                  </>
                )}
                <button
                  style={{
                  padding: '8px 14px',
                  background: '#00798E',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontWeight: '500',
                  marginTop: '16px',
                }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleProfileImageUpload('patient');
                  }}
                  disabled={profileUploading.patient}
                >
                  {profileUploading.patient ? (
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                  ) : profileUploaded.patient ? (
                    <CloudDoneIcon sx={{ color: 'white' }} />
                  ) : (
                    'Upload'
                  )}
                </button>
                {profileImageErrors.patient && (
                  <Typography variant="caption" color="error" mt={1}>
                    {profileImageErrors.patient}
                  </Typography>
                )}
              </Box>
            )}

            {/* Show existing patient profile picture with status */}
            {existingPatientProfilePicture && (
              <Box
                sx={{
                  border: '2px solid #4caf50',
                  borderRadius: 2,
                  width: 180,
                  height: 180,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#e8f5e8',
                  position: 'relative',
                }}
              >
                <img
                  src={existingPatientProfilePicture}
                  alt="Patient Profile"
                  style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: '50%', cursor: 'pointer' }}
                  onClick={() => window.open(existingPatientProfilePicture, '_blank')}
                />
                <Typography variant="caption" color="success.main" mt={1} textAlign="center">
                  ✓ Patient Photo Uploaded
                </Typography>
              </Box>
            )}

            {/* Spouse Profile Image - Only show if no existing profile picture and spouse info exists */}
            {hasSpouseInfo && !existingSpouseProfilePicture && (
              <Box
                sx={{
                  border: '2px dashed #00798E',
                  borderRadius: 2,
                  width: 180,
                  height: 180,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  background: '#F7F9FA',
                  position: 'relative',
                }}
                onClick={() => document.getElementById('spouse-profile-upload').click()}
              >
                <input
                  id="spouse-profile-upload"
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setProfileImages((prev) => ({ ...prev, spouse: file }));
                      setProfileImageErrors((prev) => ({ ...prev, spouse: '' }));
                      setProfileUploaded((prev) => ({ ...prev, spouse: false }));
                    }
                  }}
                />
                {profileImages.spouse ? (
                  <img
                    src={URL.createObjectURL(profileImages.spouse)}
                    alt="Spouse Profile"
                    style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: '50%' }}
                  />
                ) : (
                  <>
                    <CloudUploadIcon sx={{ fontSize: 40, color: '#00798E' }} />
                    <Typography variant="body2" sx={{ color: 'white' }} mt={1}>
                      Select Spouse Photo
                    </Typography>
                  </>
                )}
                <button
                  style={{
                  padding: '8px 14px',
                  background: '#00798E',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontWeight: '500',
                  marginTop: '16px',
                }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleProfileImageUpload('spouse');
                  }}
                  disabled={profileUploading.spouse}
                >
                  {profileUploading.spouse ? (
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                  ) : profileUploaded.spouse ? (
                    <CloudDoneIcon sx={{ color: 'white' }} />
                  ) : (
                    'Upload'
                  )}
                </button>
                {profileImageErrors.spouse && (
                  <Typography variant="caption" color="error" mt={1}>
                    {profileImageErrors.spouse}
                  </Typography>
                )}
              </Box>
            )}

            {/* Show existing spouse profile picture with status */}
            {hasSpouseInfo && existingSpouseProfilePicture && (
              <Box
                sx={{
                  border: '2px solid #4caf50',
                  borderRadius: 2,
                  width: 180,
                  height: 180,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#e8f5e8',
                  position: 'relative',
                }}
              >
                <img
                  src={existingSpouseProfilePicture}
                  alt="Spouse Profile"
                  style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: '50%', cursor: 'pointer' }}
                  onClick={() => window.open(existingSpouseProfilePicture, '_blank')}
                />
                <Typography variant="caption" color="success.main" mt={1} textAlign="center">
                  ✓ Spouse Photo Uploaded
                </Typography>
              </Box>
            )}

            {/* Show message if both profile pictures exist */}
            {hasSpouseInfo && existingPatientProfilePicture && existingSpouseProfilePicture && (
              <Box
                sx={{
                  border: '2px solid #4caf50',
                  borderRadius: 2,
                  width: 200,
                  height: 60,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#e8f5e8',
                }}
              >
                <Typography variant="body2" color="success.main" textAlign="center">
                  ✓ All profile pictures uploaded
                </Typography>
              </Box>
            )}
          </Box>
          {/* ...existing document upload UI... */}
          <Card sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{
                background: '#00798E',
                p: 2,
                borderRadius: '8px 8px 0 0',
                color: 'white!important',
              }}
            >
              Patient Document
            </Typography>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableBody>
                  {getAvailableDocTypes('patient').length > 0 ? (
                    getAvailableDocTypes('patient').map((doc, idx) => renderDocRow('patient', doc, idx))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="textSecondary">
                          All document types have been uploaded for this patient.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
          {hasSpouseInfo && (
            <Card>
              <Typography
                variant="h6"
                sx={{
                  background: '#00798E',
                  p: 2,
                  borderRadius: '8px 8px 0 0',
                  color: 'white!important',
                }}
              >
                Spouse Document
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableBody>
                    {getAvailableDocTypes('spouse').length > 0 ? (
                      getAvailableDocTypes('spouse').map((doc, idx) => renderDocRow('spouse', doc, idx))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="textSecondary">
                            All document types have been uploaded for the spouse.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          )}
          {/* Next Button below Spouse Document */}
          <Box display="flex" justifyContent="flex-end" mt={3} mb={2}>
            <button
              type="button"
              style={{
                padding: '10px 24px',
                background: '#00798E',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                cursor: isMandatoryUploadsDone() ? 'pointer' : 'not-allowed',
                fontSize: '1rem',
                fontWeight: '500',
                opacity: isMandatoryUploadsDone() ? 1 : 0.6,
                transition: 'opacity 0.2s',
              }}
              // disabled={!isMandatoryUploadsDone()}
              onClick={() => {
                if (!isMandatoryUploadsDone()) {
                  // Get fresh nationality value from localStorage for validation message
                  const currentNationality = localStorage.getItem('nationality');
                  Swal.fire({
                    icon: 'warning',
                    title: 'Mandatory Uploads Required',
                    text:
                      currentNationality && currentNationality.toLowerCase() === 'indian'
                        ? 'Please upload Aadhaar card.'
                        : 'Please upload Passport, Visa.',
                    confirmButtonColor: '#00798E',
                  });
                  return;
                }
                // Get fresh patient codes from localStorage for navigation
                const currentExternalCode = localStorage.getItem('external_code');
                const currentCoupleExternalCode = localStorage.getItem('couple_external_code');
                // Redirect to contact-histories/{external_code}/{couple_external_code}
                window.location.href = `/contact-histories/${currentExternalCode}/${currentCoupleExternalCode}`;
              }}
            >
              Next
            </button>
          </Box>
        </Box>
      )}
      <Footer />
    </form>
  );
}

DocumentUpload.propTypes = {
  label: PropTypes.string,
  onChange: PropTypes.func,
  accept: PropTypes.string,
  multiple: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.arrayOf(PropTypes.object)]),
  existingPatientDocuments: PropTypes.arrayOf(PropTypes.object),
  existingSpouseDocuments: PropTypes.arrayOf(PropTypes.object),
  existingPatientProfilePicture: PropTypes.string,
  existingSpouseProfilePicture: PropTypes.string,
  hasSpouseInfo: PropTypes.bool,
};
